/**
 * Rolling ring buffer for recent client-side errors.
 *
 * Installed once at app startup. Captures `window.onerror`,
 * `unhandledrejection`, and `console.error` calls into a bounded
 * FIFO. The feedback pill reads the buffer at submit time and
 * attaches it to the payload so triage in the admin panel has
 * concrete evidence of any JS crashes the user just saw.
 *
 * Kept small on purpose:
 *   - max 20 entries (older ones drop off)
 *   - each message truncated to 400 chars
 *   - only entries within the last 30s are returned to callers
 *
 * The buffer lives on `window` so it survives React remounts.
 */

const MAX_ENTRIES = 20;
const WINDOW_MS = 30 * 1000;
const MAX_MSG_LEN = 400;

let installed = false;

function pushEntry(level, message) {
    try {
        const buf = window.__bs_error_buffer || (window.__bs_error_buffer = []);
        const truncated = String(message).slice(0, MAX_MSG_LEN);
        buf.push({ ts: new Date().toISOString(), level, message: truncated });
        if (buf.length > MAX_ENTRIES) buf.splice(0, buf.length - MAX_ENTRIES);
    } catch (_) { /* swallow — must never throw from an error handler */ }
}

export function installConsoleErrorBuffer() {
    if (installed || typeof window === 'undefined') return;
    installed = true;

    // Global JS errors
    window.addEventListener('error', (event) => {
        const src = event?.filename ? ` (${event.filename}:${event.lineno || 0})` : '';
        pushEntry('error', `${event?.message || 'error'}${src}`);
    });

    // Unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
        const reason = event?.reason;
        const msg = reason?.message || String(reason || 'unhandled rejection');
        pushEntry('unhandledrejection', msg);
    });

    // console.error interception — preserves the original console
    // behaviour so we don't hide anything from devtools.
    const origError = console.error;
    console.error = function (...args) {
        try {
            const parts = args.map((a) => {
                if (a instanceof Error) return a.stack || a.message;
                if (typeof a === 'object') { try { return JSON.stringify(a); } catch (_) { return String(a); } }
                return String(a);
            });
            pushEntry('error', parts.join(' '));
        } catch (_) { /* keep original error path unaffected */ }
        return origError.apply(this, args);
    };
}

/** Returns entries captured within the last WINDOW_MS. */
export function getRecentConsoleErrors() {
    try {
        const buf = (typeof window !== 'undefined' && window.__bs_error_buffer) || [];
        const cutoff = Date.now() - WINDOW_MS;
        return buf.filter((e) => {
            const t = e?.ts ? Date.parse(e.ts) : 0;
            return t >= cutoff;
        });
    } catch (_) {
        return [];
    }
}
